import type { AudioAssetRef } from '@/domain/types';
import type { StorageProvider } from '@/storage/StorageProvider';
import { newAssetId } from '@/util/id';
import { computePeaks, peaksByteLength } from './waveform';
import type { WaveformPeaks } from './waveformTypes';
import type { WaveformRequest, WaveformResponse } from './waveform.worker';

/**
 * Owns decoded audio.
 *
 * Two caches sit here, both budgeted, because AudioBuffers are the largest
 * objects in the application by an order of magnitude: one minute of 48kHz
 * stereo is 23MB of Float32 whether or not the user is looking at it. An
 * unbounded cache is how a browser DAW ends up killed by the tab's memory
 * limiter twenty minutes into a session.
 *
 * The cache is keyed by asset id, evicts least-recently-used entries, and never
 * evicts anything currently referenced by playing audio — the scheduler holds
 * its own reference to any buffer it has handed to a source node, so eviction
 * cannot silence a playing clip.
 */

export interface ImportResult {
  asset: AudioAssetRef;
  buffer: AudioBuffer;
  peaks: WaveformPeaks;
}

export class AudioImportError extends Error {
  constructor(
    readonly reason: 'unsupported' | 'corrupt' | 'tooLarge' | 'empty' | 'storage',
    message: string,
  ) {
    super(message);
    this.name = 'AudioImportError';
  }
}

const DEFAULT_BUFFER_BUDGET_BYTES = 512 * 1024 * 1024;
const DEFAULT_PEAKS_BUDGET_BYTES = 64 * 1024 * 1024;
const MAX_IMPORT_BYTES = 1024 * 1024 * 1024;

interface CacheEntry<T> {
  value: T;
  bytes: number;
  lastUsed: number;
}

export class AssetManager {
  private buffers = new Map<string, CacheEntry<AudioBuffer>>();
  private peaks = new Map<string, CacheEntry<WaveformPeaks>>();
  private pendingBuffers = new Map<string, Promise<AudioBuffer | null>>();
  private pendingPeaks = new Map<string, Promise<WaveformPeaks | null>>();
  private worker: Worker | null = null;
  private workerRequests = new Map<number, (response: WaveformResponse) => void>();
  private nextRequestId = 1;
  private clock = 0;

  constructor(
    private readonly ctx: BaseAudioContext,
    readonly storage: StorageProvider,
    private readonly bufferBudget = DEFAULT_BUFFER_BUDGET_BYTES,
    private readonly peaksBudget = DEFAULT_PEAKS_BUDGET_BYTES,
  ) {}

  // -- import --------------------------------------------------------------

  /**
   * Imports a file: validate, persist the original bytes, decode, and build
   * peaks. The original encoded file is what gets stored — not the decoded
   * samples — so a 4MB MP3 costs 4MB on disk rather than 50MB, and re-exporting
   * a project can hand back the exact file the user imported.
   */
  async importFile(file: File | Blob, name: string): Promise<ImportResult> {
    if (file.size === 0) throw new AudioImportError('empty', `"${name}" is empty.`);
    if (file.size > MAX_IMPORT_BYTES) {
      throw new AudioImportError(
        'tooLarge',
        `"${name}" is ${(file.size / 1024 / 1024).toFixed(0)}MB, larger than the ${MAX_IMPORT_BYTES / 1024 / 1024}MB import limit.`,
      );
    }

    const bytes = await file.arrayBuffer();
    // decodeAudioData detaches the buffer it is given, so decode from a copy and
    // keep the original for storage.
    const buffer = await this.decode(bytes.slice(0), name);

    const asset: AudioAssetRef = {
      id: newAssetId(),
      name: stripExtension(name),
      originalFileName: name,
      mimeType: file.type || guessMimeType(name),
      byteLength: file.size,
      sampleRate: buffer.sampleRate,
      channelCount: buffer.numberOfChannels,
      durationSec: buffer.duration,
      createdAt: Date.now(),
      recorded: false,
    };

    try {
      await this.storage.putAsset(asset, file instanceof Blob ? file : new Blob([bytes]));
    } catch (error) {
      throw new AudioImportError(
        'storage',
        error instanceof Error ? error.message : `Could not store "${name}" locally.`,
      );
    }

    this.putBuffer(asset.id, buffer);
    const peaks = await this.buildPeaks(asset.id, buffer);
    return { asset, buffer, peaks };
  }

  /** Registers audio that was captured rather than imported (recording takes). */
  async registerRecording(
    buffer: AudioBuffer,
    encoded: Blob,
    name: string,
  ): Promise<ImportResult> {
    const asset: AudioAssetRef = {
      id: newAssetId(),
      name,
      originalFileName: null,
      mimeType: encoded.type || 'audio/wav',
      byteLength: encoded.size,
      sampleRate: buffer.sampleRate,
      channelCount: buffer.numberOfChannels,
      durationSec: buffer.duration,
      createdAt: Date.now(),
      recorded: true,
    };
    await this.storage.putAsset(asset, encoded);
    this.putBuffer(asset.id, buffer);
    const peaks = await this.buildPeaks(asset.id, buffer);
    return { asset, buffer, peaks };
  }

  private async decode(bytes: ArrayBuffer, name: string): Promise<AudioBuffer> {
    try {
      const buffer = await this.ctx.decodeAudioData(bytes);
      if (buffer.length === 0) throw new AudioImportError('empty', `"${name}" decoded to no audio.`);
      return buffer;
    } catch (error) {
      if (error instanceof AudioImportError) throw error;
      throw new AudioImportError(
        'unsupported',
        `"${name}" could not be decoded. This browser can play WAV, MP3, M4A/AAC and OGG; ` +
          'other formats (and damaged files) will not open. Converting the file to WAV usually fixes it.',
      );
    }
  }

  // -- retrieval -----------------------------------------------------------

  /** Decoded audio for an asset, decoding from storage on first use. */
  async getBuffer(assetId: string): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(assetId);
    if (cached) {
      cached.lastUsed = ++this.clock;
      return cached.value;
    }

    const inFlight = this.pendingBuffers.get(assetId);
    if (inFlight) return inFlight;

    const promise = (async (): Promise<AudioBuffer | null> => {
      const stored = await this.storage.getAsset(assetId);
      if (!stored) return null;
      const bytes = await stored.blob.arrayBuffer();
      try {
        const buffer = await this.ctx.decodeAudioData(bytes);
        this.putBuffer(assetId, buffer);
        return buffer;
      } catch {
        return null;
      } finally {
        this.pendingBuffers.delete(assetId);
      }
    })();

    this.pendingBuffers.set(assetId, promise);
    return promise;
  }

  /** Peaks for an asset, from memory, then the on-disk cache, then computed. */
  async getPeaks(assetId: string): Promise<WaveformPeaks | null> {
    const cached = this.peaks.get(assetId);
    if (cached) {
      cached.lastUsed = ++this.clock;
      return cached.value;
    }

    const inFlight = this.pendingPeaks.get(assetId);
    if (inFlight) return inFlight;

    const promise = (async (): Promise<WaveformPeaks | null> => {
      try {
        const stored = await this.storage.getWaveform(assetId);
        if (stored) {
          this.putPeaks(assetId, stored);
          return stored;
        }
        const buffer = await this.getBuffer(assetId);
        if (!buffer) return null;
        return await this.buildPeaks(assetId, buffer);
      } finally {
        this.pendingPeaks.delete(assetId);
      }
    })();

    this.pendingPeaks.set(assetId, promise);
    return promise;
  }

  /** Synchronous peek used by the timeline renderer, which cannot await. */
  peekPeaks(assetId: string): WaveformPeaks | null {
    const entry = this.peaks.get(assetId);
    if (!entry) return null;
    entry.lastUsed = ++this.clock;
    return entry.value;
  }

  peekBuffer(assetId: string): AudioBuffer | null {
    const entry = this.buffers.get(assetId);
    if (!entry) return null;
    entry.lastUsed = ++this.clock;
    return entry.value;
  }

  // -- peaks ---------------------------------------------------------------

  private async buildPeaks(assetId: string, buffer: AudioBuffer): Promise<WaveformPeaks> {
    const channels: Float32Array[] = [];
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      // Copy: the worker transfers what it is given, and detaching a live
      // AudioBuffer's channel data would corrupt playback.
      const copy = new Float32Array(buffer.length);
      buffer.copyFromChannel(copy, c);
      channels.push(copy);
    }

    const peaks = await this.requestPeaks({
      requestId: this.nextRequestId++,
      assetId,
      channels,
      sampleRate: buffer.sampleRate,
      durationSec: buffer.duration,
    });

    this.putPeaks(assetId, peaks);
    void this.storage.putWaveform(assetId, peaks).catch(() => {
      // A failed peak cache write is not worth interrupting the user for; the
      // peaks are already in memory and will simply be recomputed next session.
    });
    return peaks;
  }

  private requestPeaks(request: WaveformRequest): Promise<WaveformPeaks> {
    const worker = this.ensureWorker();
    if (!worker) {
      // No worker available (strict CSP, or a test environment): compute on the
      // main thread. Slower and it can drop a frame, but it always works.
      return Promise.resolve(
        computePeaks(request.assetId, request.channels, request.sampleRate, request.durationSec),
      );
    }

    return new Promise<WaveformPeaks>((resolve, reject) => {
      this.workerRequests.set(request.requestId, (response) => {
        if (response.peaks) resolve(response.peaks);
        else reject(new Error(response.error ?? 'Waveform generation failed.'));
      });
      worker.postMessage(
        request,
        request.channels.map((c) => c.buffer as ArrayBuffer),
      );
    });
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    try {
      this.worker = new Worker(new URL('./waveform.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (event: MessageEvent<WaveformResponse>) => {
        const handler = this.workerRequests.get(event.data.requestId);
        this.workerRequests.delete(event.data.requestId);
        handler?.(event.data);
      };
      this.worker.onerror = () => {
        this.worker?.terminate();
        this.worker = null;
      };
    } catch {
      this.worker = null;
    }
    return this.worker;
  }

  // -- cache management ----------------------------------------------------

  private putBuffer(assetId: string, buffer: AudioBuffer): void {
    const bytes = buffer.length * buffer.numberOfChannels * 4;
    this.buffers.set(assetId, { value: buffer, bytes, lastUsed: ++this.clock });
    this.evict(this.buffers, this.bufferBudget);
  }

  private putPeaks(assetId: string, peaks: WaveformPeaks): void {
    this.peaks.set(assetId, { value: peaks, bytes: peaksByteLength(peaks), lastUsed: ++this.clock });
    this.evict(this.peaks, this.peaksBudget);
  }

  private evict<T>(cache: Map<string, CacheEntry<T>>, budget: number): void {
    let total = 0;
    for (const entry of cache.values()) total += entry.bytes;
    if (total <= budget) return;

    const byAge = [...cache.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [key, entry] of byAge) {
      if (total <= budget) break;
      if (this.pinned.has(key)) continue;
      cache.delete(key);
      total -= entry.bytes;
    }
  }

  private pinned = new Set<string>();

  /** Prevents an asset from being evicted while it is scheduled for playback. */
  pin(assetId: string): void {
    this.pinned.add(assetId);
  }

  unpin(assetId: string): void {
    this.pinned.delete(assetId);
  }

  memoryUsage(): { bufferBytes: number; peakBytes: number } {
    let bufferBytes = 0;
    let peakBytes = 0;
    for (const entry of this.buffers.values()) bufferBytes += entry.bytes;
    for (const entry of this.peaks.values()) peakBytes += entry.bytes;
    return { bufferBytes, peakBytes };
  }

  /** Releases every cached object. Called when a project closes. */
  clear(): void {
    this.buffers.clear();
    this.peaks.clear();
    this.pendingBuffers.clear();
    this.pendingPeaks.clear();
    this.pinned.clear();
  }

  dispose(): void {
    this.clear();
    this.worker?.terminate();
    this.worker = null;
    this.workerRequests.clear();
  }
}

function stripExtension(name: string): string {
  const index = name.lastIndexOf('.');
  return index > 0 ? name.slice(0, index) : name;
}

function guessMimeType(name: string): string {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  switch (ext) {
    case 'wav':
      return 'audio/wav';
    case 'mp3':
      return 'audio/mpeg';
    case 'm4a':
    case 'aac':
      return 'audio/mp4';
    case 'ogg':
    case 'oga':
      return 'audio/ogg';
    case 'flac':
      return 'audio/flac';
    default:
      return 'application/octet-stream';
  }
}
